import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  CalendarClock,
  RefreshCw,
  Home,
  ArrowRight,
  ShieldCheck,
  Info,
  Users,
} from 'lucide-react';

const BOOKING_ROUTE = '/?page=booking';
const HOME_ROUTE = 'https://svheinersreuth-padel.de/';

export default function BookingSlotUnavailablePage() {
  const [navigating, setNavigating] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const goToBooking = () => {
    if (navigating) return;
    setNavigating(true);
    window.location.href = BOOKING_ROUTE;
  };

  const goHome = () => {
    if (navigating) return;
    setNavigating(true);
    window.location.href = HOME_ROUTE;
  };

  return (
    <div className="min-h-screen svh-canvas flex flex-col items-center justify-center px-4 py-16">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-[560px]"
      >
        {/* Brand header */}
        <div className="text-center mb-8">
          <img
            src="/photos/Image.jpeg"
            alt="SV Heinersreuth"
            className="h-14 w-14 rounded-full object-cover mx-auto mb-3 shadow-sm ring-1 ring-black/5"
          />
          <p className="text-[15px] font-bold tracking-tight" style={{ color: 'var(--svh-ink)' }}>
            SV Heinersreuth
          </p>
          <p className="svh-label mt-1">Padel &amp; Tennis</p>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          className="svh-card svh-card-lg svh-card-raised overflow-hidden"
        >
          <div className="h-1 w-full" style={{ background: 'var(--svh-crest)' }} />
          <div className="p-8 md:p-10">

            {/* Icon */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, delay: 0.2 }}
              className="flex items-center justify-center mb-6"
            >
              <div className="w-16 h-16 rounded-full bg-amber-50 flex items-center justify-center">
                <CalendarClock className="w-8 h-8 text-amber-600" strokeWidth={1.75} />
              </div>
            </motion.div>

            {/* Headline */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.25 }}
              className="text-center mb-8"
            >
              <p className="text-[12px] font-bold tracking-[0.16em] uppercase text-amber-700 mb-3">
                Buchung nicht abgeschlossen
              </p>
              <h1 className="svh-h2 mb-4">Dieser Termin wurde gerade vergeben</h1>
              <p className="svh-body">
                Leider hat eine andere Person den Platz nahezu im selben Moment gebucht.
                Deshalb konnte Ihre Buchung nicht abgeschlossen werden.
              </p>
            </motion.div>

            {/* What happened */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.3 }}
              className="rounded-[10px] px-5 py-4 mb-4"
              style={{ background: 'var(--svh-surface-sub)', border: '1px solid var(--svh-border)' }}
            >
              <div className="flex items-start gap-3">
                <Users className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: 'var(--svh-ink-3)' }} strokeWidth={2} />
                <div>
                  <p className="svh-label mb-1">Zeitgleich gebucht</p>
                  <p className="text-[14px] leading-relaxed" style={{ color: 'var(--svh-ink-2)' }}>
                    Ein anderer Kunde hat den gleichen Platz fast im selben Augenblick
                    für sich gebucht und die Buchung minimal früher abgeschlossen.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Refund information */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.35 }}
              className="rounded-[10px] overflow-hidden mb-6"
              style={{ border: '1px solid var(--svh-border)' }}
            >
              <div className="px-5 py-4" style={{ background: 'var(--svh-surface-sub)' }}>
                <div className="flex items-start gap-3">
                  <Info className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: 'var(--svh-ink-3)' }} strokeWidth={2} />
                  <div>
                    <p className="svh-label mb-1">Rückerstattung</p>
                    <p className="text-[14px] leading-relaxed mb-1.5" style={{ color: 'var(--svh-ink-2)' }}>
                      Falls Sie bereits bezahlt haben, wurde der vollständige Betrag
                      automatisch zurückerstattet.
                    </p>
                    <p className="svh-meta">
                      Je nach Zahlungsanbieter kann es einige Werktage dauern, bis die
                      Rückzahlung auf Ihrem Konto sichtbar ist.
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-3 px-5 py-4 bg-emerald-50/60 border-t" style={{ borderColor: 'var(--svh-border)' }}>
                <ShieldCheck className="w-4 h-4 text-emerald-700 flex-shrink-0 mt-0.5" strokeWidth={2} />
                <p className="text-[13px] leading-relaxed" style={{ color: 'var(--svh-ink-2)' }}>
                  Es wurde keine Doppelbuchung erstellt.
                </p>
              </div>
            </motion.div>

            <hr className="svh-rule mb-6" />

            {/* Actions */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.4 }}
              className="space-y-3"
            >
              <button
                onClick={goToBooking}
                disabled={navigating}
                className="svh-btn svh-btn--primary svh-focus w-full"
              >
                <RefreshCw className="w-4 h-4" />
                Neue Zeit auswählen
              </button>
              <button
                onClick={goHome}
                disabled={navigating}
                className="svh-btn svh-btn--secondary svh-focus w-full"
              >
                <Home className="w-4 h-4" />
                Zur Startseite
                <ArrowRight className="w-4 h-4" />
              </button>
            </motion.div>
          </div>
        </motion.div>

        <p className="svh-meta text-center mt-6">SV Heinersreuth e.V.</p>
      </motion.div>
    </div>
  );
}
